import { useId, type ReactNode } from 'react';
import { LiksBusyLabel } from '../LiksBusyLabel/LiksBusyLabel';
import { LiksButton, type LiksButtonVariant } from '../LiksButton/LiksButton';
import { LiksDialog } from '../LiksDialog/LiksDialog';
import './LiksConfirmDialog.css';

export type LiksConfirmDialogSecondaryAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Shows a spinner on the secondary button (e.g. in-progress download). */
  isBusy?: boolean;
};

export type LiksConfirmDialogProps = {
  isOpen: boolean;
  title: ReactNode;
  message: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * Blocks Escape / backdrop / cancel / close while a long-running action is
   * in flight (confirm or secondary).
   */
  isBusy?: boolean;
  /**
   * Shows the busy spinner on the confirm button. Defaults to `isBusy` when
   * the secondary action is not busy (keeps existing delete/confirm UX).
   */
  isConfirmBusy?: boolean;
  /** Extra disable for confirm (e.g. waiting for a download to finish). */
  isConfirmDisabled?: boolean;
  /** Confirm button color. Default `primary`. */
  confirmVariant?: Extract<LiksButtonVariant, 'primary' | 'danger'>;
  /** Optional middle action (e.g. Download) between Cancel and Confirm. */
  secondaryAction?: LiksConfirmDialogSecondaryAction;
  closeLabel?: string;
};

export function LiksConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  isBusy = false,
  isConfirmBusy,
  isConfirmDisabled = false,
  confirmVariant = 'primary',
  secondaryAction,
  closeLabel,
}: LiksConfirmDialogProps) {
  const messageId = useId();
  const showConfirmBusy =
    isConfirmBusy ?? (isBusy && secondaryAction?.isBusy !== true);
  const isConfirmActionDisabled =
    isBusy || isConfirmDisabled || showConfirmBusy;

  if (!isOpen) {
    return null;
  }

  return (
    <LiksDialog
      isOpen
      onClose={onCancel}
      title={title}
      size="md"
      role="alertdialog"
      aria-describedby={messageId}
      closeLabel={closeLabel}
      isDismissDisabled={isBusy}
      closeDisabled={isBusy}
      bodyClassName="liks-confirm-dialog__body"
      footer={
        <>
          <LiksButton variant="secondary" disabled={isBusy} onClick={onCancel}>
            {cancelLabel}
          </LiksButton>
          {secondaryAction != null && (
            <LiksButton
              variant="secondary"
              className={
                secondaryAction.isBusy === true
                  ? 'liks-button--busy'
                  : undefined
              }
              disabled={
                isBusy ||
                secondaryAction.disabled === true ||
                secondaryAction.isBusy === true
              }
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.isBusy === true ? (
                <LiksBusyLabel>{secondaryAction.label}</LiksBusyLabel>
              ) : (
                secondaryAction.label
              )}
            </LiksButton>
          )}
          <LiksButton
            variant={confirmVariant}
            className={showConfirmBusy ? 'liks-button--busy' : undefined}
            disabled={isConfirmActionDisabled}
            onClick={onConfirm}
          >
            {showConfirmBusy ? (
              <LiksBusyLabel>{confirmLabel}</LiksBusyLabel>
            ) : (
              confirmLabel
            )}
          </LiksButton>
        </>
      }
    >
      {typeof message === 'string' ? (
        <p id={messageId} className="liks-confirm-dialog__message">
          {message}
        </p>
      ) : (
        <div id={messageId} className="liks-confirm-dialog__message">
          {message}
        </div>
      )}
    </LiksDialog>
  );
}
